import { useChatReelStore, type ChatReelSelectionDraft, type MuseModelList } from '@/stores/chat-reel';
import { AUTO_ASSIGN_MODEL_ID, findModelById, getModelParameterConfigs, getSelectedOption, normalizeModelList, resolveDurationValue, resolveReelSelectionDraft } from './modelSelection';

type SetReelSelectionOptions = {
  replace?: boolean;
  markChanged?: boolean;
};

export const applyReelModelList = (sourceModelList: MuseModelList) => {
  const state = useChatReelStore.getState();
  const modelList = normalizeModelList(sourceModelList);
  const storeSelectionDraft = resolveReelSelectionDraft(modelList, state.storeSelectionDraft);

  useChatReelStore.setState({
    modelList,
    storeSelectionDraft,
    selectionModeInfo: null,
  });
};

export const setReelSelection = (draft: Partial<ChatReelSelectionDraft>, options: SetReelSelectionOptions = {}) => {
  const state = useChatReelStore.getState();
  const modelList = normalizeModelList(state.modelList);
  const currentDraft = resolveReelSelectionDraft(modelList, state.storeSelectionDraft);
  const candidateDraft = options.replace ? draft : { ...currentDraft, ...draft };
  const storeSelectionDraft = resolveReelSelectionDraft(modelList, candidateDraft as ChatReelSelectionDraft);

  useChatReelStore.setState({
    storeSelectionDraft,
    selectionModeInfo: null,
    ...(options.markChanged === false ? {} : { historySessionChanged: true }),
  });

  return storeSelectionDraft;
};

export const selectReelModel = (modelId: string) => {
  const state = useChatReelStore.getState();
  const modelList = normalizeModelList(state.modelList);
  const selectedModel = findModelById(modelList, modelId);

  if (!selectedModel) return false;

  if (modelId === AUTO_ASSIGN_MODEL_ID) {
    const autoDraft = {
      activeModelId: AUTO_ASSIGN_MODEL_ID,
      modelByGroup: { auto: AUTO_ASSIGN_MODEL_ID },
    };
    const { durationRange } = getModelParameterConfigs(modelList, autoDraft);
    setReelSelection(
      {
        ...autoDraft,
        imageUsage: undefined,
        aspectRatio: getSelectedOption(modelList.aspect_ratios)?.value,
        resolution: getSelectedOption(modelList.resolutions)?.value,
        duration: resolveDurationValue(durationRange),
        audio: getSelectedOption(modelList.audio)?.value,
      },
      { replace: true },
    );
    return true;
  }

  const currentDraft = resolveReelSelectionDraft(modelList, state.storeSelectionDraft);
  const nextModelByGroup = {
    ...currentDraft.modelByGroup,
    [selectedModel.groupKey]: modelId,
  };
  delete nextModelByGroup.auto;

  setReelSelection({
    activeModelId: modelId,
    modelByGroup: nextModelByGroup,
  });
  return true;
};
